/**
 * Chip de situação do posto avaliado (#356): sempre a palavra, nunca só a cor. Posto inativo mostra
 * "Inativo" e não tem situação calculada.
 */
import React from 'react';
import { Chip, type SxProps, type Theme } from '@mui/material';
import {
  CheckCircle as DoneIcon,
  HourglassEmpty as DraftIcon,
  PauseCircleOutline as InactiveIcon,
  WarningAmber as OpenActionsIcon,
} from '@mui/icons-material';

import type { ErgonomicSituation, ErgonomicWorkstationStatus } from '../../types/ergonomicWorkstation';
import { INACTIVE_LABEL, SITUATION_LABELS } from './situationLabels';

type Tone = 'info' | 'warning' | 'success' | 'neutral';

interface ChipDescriptor {
  label: string;
  tone: Tone;
  icon: React.ReactElement;
}

const SITUATION_CHIPS: Record<ErgonomicSituation, ChipDescriptor> = {
  EM_CADASTRO: { label: SITUATION_LABELS.EM_CADASTRO, tone: 'info', icon: <DraftIcon /> },
  ACOES_EM_ABERTO: { label: SITUATION_LABELS.ACOES_EM_ABERTO, tone: 'warning', icon: <OpenActionsIcon /> },
  CONCLUIDO: { label: SITUATION_LABELS.CONCLUIDO, tone: 'success', icon: <DoneIcon /> },
};

const INACTIVE_CHIP: ChipDescriptor = { label: INACTIVE_LABEL, tone: 'neutral', icon: <InactiveIcon /> };

/**
 * Texto e borda no tom escuro da paleta (o `main` do aviso e do sucesso fica abaixo de 4,5:1 sobre o
 * branco) e letra de 16px (a situação é a informação principal da linha). No tema escuro volta o tom claro.
 */
const readableChipSx =
  (tone: Tone): SxProps<Theme> =>
  (theme) => {
    const dark = tone === 'neutral' ? theme.palette.text.primary : theme.palette[tone].dark;
    const light = tone === 'neutral' ? theme.palette.text.primary : theme.palette[tone].light;
    return {
      fontSize: '1rem',
      height: 'auto',
      minHeight: 32,
      fontWeight: 500,
      color: dark,
      borderColor: dark,
      '& .MuiChip-icon': { color: 'inherit' },
      ...theme.applyStyles('dark', { color: light, borderColor: light }),
    };
  };

interface SituationChipProps {
  status: ErgonomicWorkstationStatus;
  situation: ErgonomicSituation | null;
}

/** Posto inativo não calcula situação; ativo sem situação (resposta incompleta) cai em "Em cadastro". */
function describe(status: ErgonomicWorkstationStatus, situation: ErgonomicSituation | null): ChipDescriptor {
  if (status === 'INACTIVE') return INACTIVE_CHIP;
  return SITUATION_CHIPS[situation ?? 'EM_CADASTRO'];
}

export const SituationChip: React.FC<SituationChipProps> = ({ status, situation }) => {
  const descriptor = describe(status, situation);
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={descriptor.icon}
      label={descriptor.label}
      sx={readableChipSx(descriptor.tone)}
    />
  );
};

export default SituationChip;
